import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { approveAiAccessRequest } from '../../../../Api/request/aiAccessReq';
import SearchDropdown from '../../../../Components/Dropdown/dropdown';
import CustomStyledDatePicker from '../../../../Components/Datepicker/CustomStyledDatePicker';

const STATUS_OPTIONS = [
  { label: 'Approve', value: 1 },
  { label: 'Reject', value: 2 },
];
const getCurrentDateTime = () => dayjs().toISOString();
export const getApprovalDialog = ({
  fieldPath,
  action,
  value,
  rowData,
}) => {
  const field = String(fieldPath || action || '').toLowerCase();

  const approvalLevel = field.includes('l1')
    ? 'L1'
    : field.includes('l2')
      ? 'L2'
      : null;

  // Get status from the supplied value or the nested row data.
  const status
    = typeof value === 'object' && value !== null
      ? value.label ?? value.name ?? value.value
      : value
        ?? (approvalLevel === 'L1'
          ? rowData?.l1_approver_details?.l1_action_status
          : approvalLevel === 'L2'
            ? rowData?.l2_approver_details?.l2_action_status
            : null);

  if (
    !approvalLevel
    || !field.includes('status')
    || String(status ?? '').trim()
      .toLowerCase() !== 'open'
  ) {
    return null;
  }

  // Support the access type from the actual row structure.
  const type
    = rowData?.access_type
    ?? rowData?.req_access_type
    ?? rowData?.request_type_name;

  const normalizedType = String(type ?? '').trim()
    .toLowerCase();

  if (!['grant access', 'revoke access'].includes(normalizedType)) {
    console.error('Unable to open approval dialog: invalid access type.', {
      fieldPath,
      value,
      rowData,
      type,
    });

    return null;
  }

  return {
    rowData,
    approvalLevel,
    requestType:
      normalizedType === 'grant access' ? 'Grant Access' : 'Revoke Access',
  };
};
const splitEmployeeDetails = (rowData, level) => {
  const l1Details = rowData?.[`${level}_approver_details`].split('-') || [];
  return l1Details;
};

const ApprovalStatusModal = ({
  requestType, approvalLevel, requestId, rowData, userProfile, onClose, onSuccess,
}) => {
  const level = approvalLevel === 'L1' ? 'l1' : 'l2';
  const approverEmpId = rowData?.[`${level}_approver_emp_id`] ?? splitEmployeeDetails(rowData, level)[0] ?? null ;
  const approverName = rowData?.[`${level}_approver_emp_name`] ?? splitEmployeeDetails(rowData, level)[1] ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [providedDate, setProvidedDate] = useState(() => dayjs());
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const title = `Request Status - ${requestType}`;

  useEffect(() => {
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, []);

  const handleSubmit = async () => {
    if (!status || !comment.trim()) {
      setError('Please select a request status and enter a comment.');
      return;
    }
    if (requestId === undefined || requestId === null || requestId === ''
      || !userProfile?.employeeId || !approverEmpId) {
      toast.error('Unable to update approval: missing request or user details.');
      return;
    }
    setError('');
    const now = dayjs();
    const approvedDateTime = now.toISOString();
    setIsSubmitting(true);
    try {
      const response = await approveAiAccessRequest({
        authToken: userProfile?.access_token || '',
        requestId,
        approvalLevel,
        currentEmployeeId: userProfile.employeeId,
        currentEmployeeEmailId: userProfile?.emailId || userProfile?.email || '',
        isSuperAdmin: userProfile?.
          roleDetails?.some(role => role?.roleName?.trim().toLowerCase() === 'super admin') || false,
        approverEmpId,
        approverName,
        requestStatusId: status.value,
        comment: comment.trim(),
        actionDateAndTime: getCurrentDateTime(),
        approvedDateTime,
      });
      if (response?.data instanceof Error
         || response?.data?.success === false || !response?.status || response.status >= 400) {
        throw response?.data instanceof Error ? response.data : new Error(response?.data?.message);
      }
      toast.success(response?.data?.message || 'AI access request status updated successfully.');
      setIsSubmitting(false);
      onSuccess();
    } catch (submitError) {
      toast.error(submitError?.response?.data?.message || submitError?.message
        || 'Failed to update AI access request status.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-ai-access-req-modal-backdrop" onClick={onClose}>
      <div
        className="add-ai-access-req-modal ai-access-req-approval-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${title} - ${approvalLevel}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-content-header">
          <h2>{title}</h2>
        </div>
        <div className="modal-content-body">
          <div className="add-ai-access-req-input-wrapper">
            <span className="required-label">Request Status<span className="asteric">*</span></span>
            <SearchDropdown
              options={STATUS_OPTIONS}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              placeholder="Select"
              searchBox={false}
              dropdownCss="add-ai-access-req-search-dropdown"
            />
          </div>
          <div className="add-ai-access-req-input-wrapper">
            <span className="required-label">Request provided date</span>
            <CustomStyledDatePicker
              value={providedDate}
              onChange={setProvidedDate}
              placeholder="Choose"
              isDisabled
            />
          </div>
          <div className="add-ai-access-req-input-wrapper">
            <label htmlFor="ai-access-approval-comment" className="required-label">
              Comment<span className="asteric">*</span>
            </label>
            <textarea
              id="ai-access-approval-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Enter..."
              required
            />
            {error && <span className="ai-access-req-approval-error" role="alert">{error}</span>}
          </div>
        </div>
        <div className="modal-content-footer">
          <div className="footer-btn-wrapper">
            <Button label="Cancel" onClick={onClose} className="p-button-cancel" text disabled={isSubmitting} />
            <Button label="Save" onClick={handleSubmit} className="p-button-submit"
              loading={isSubmitting} disabled={isSubmitting} />
          </div>
        </div>
      </div>
    </div>
  );
};

ApprovalStatusModal.propTypes = {
  requestType: PropTypes.oneOf(['Grant Access', 'Revoke Access']).isRequired,
  approvalLevel: PropTypes.oneOf(['L1', 'L2']).isRequired,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowData: PropTypes.objectOf(PropTypes.any),
  userProfile: PropTypes.shape({
    employeeId: PropTypes.string,
    emailId: PropTypes.string,
    roleDetails: PropTypes.arrayOf(
      PropTypes.shape({
        roleName: PropTypes.string,
      }),
    ),
    name: PropTypes.string,
    email: PropTypes.string,
    access_token: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

ApprovalStatusModal.defaultProps = {
  requestId: null,
  rowData: null,
  userProfile: null,
};

export default ApprovalStatusModal;
